<template>
    <div>
        <div v-show="isReady">
            <!-- En-tête (même présentation que la fiche boutique) -->
            <div class="card mb-4">
                <div class="card-body py-4 px-6">
                    <div class="d-flex align-items-center justify-content-between flex-wrap gap-3">
                        <div class="d-flex align-items-center gap-3">
                            <div class="symbol symbol-45px">
                                <span class="symbol-label bg-light-primary">
                                    <i class="bi bi-building fs-3 text-primary"></i>
                                </span>
                            </div>
                            <div>
                                <div class="d-flex align-items-center gap-2">
                                    <span class="text-gray-900 fs-4 fw-bolder">{{ store.name }}</span>
                                    <span v-if="store.etat == 'active'" class="badge badge-light-success">
                                        <i class="bi bi-check-circle fs-9 me-1"></i>{{ $t('shop.active') }}
                                    </span>
                                    <span v-else class="badge badge-light-danger">
                                        <i class="bi bi-archive fs-9 me-1"></i>{{ $t('shop.inactive') }}
                                    </span>
                                </div>
                                <div class="d-flex flex-wrap gap-4 mt-1">
                                    <span v-if="store.phone" class="text-gray-400 fs-7">
                                        <i class="bi bi-telephone me-1"></i>{{ store.phone }}
                                    </span>
                                    <span v-if="store.address" class="text-gray-400 fs-7">
                                        <i class="bi bi-geo-alt me-1"></i>{{ store.address }}
                                    </span>
                                </div>
                            </div>
                        </div>

                        <div class="d-flex gap-2 flex-wrap">
                            <template v-if="store.etat == 'active'">
                                <a v-if="$can('magasin_update')" @click="startModalUpdate" class="btn btn-sm btn-primary">{{ $t('store.update.title') }}</a>
                                <a v-if="isUsing && $can('magasin_delete')" @click="remove" class="btn btn-sm btn-danger">{{ $t('store.deleted') }}</a>
                                <a v-else-if="!isUsing && $can('magasin_archive')" @click="archive" class="btn btn-sm btn-light">{{ $t('store.archive') }}</a>
                            </template>
                            <a v-else-if="$can('magasin_archive')" @click="active" class="btn btn-sm btn-light">{{ $t('store.unarchive') }}</a>
                        </div>
                    </div>
                </div>
            </div>

            <!-- Menu gauche + contenu droite -->
            <div class="row g-5 mt-1">
                <div class="col-lg-3 col-md-3">
                    <div class="card card-flush">
                        <div class="card-body p-3">
                            <div class="menu menu-column menu-rounded menu-gray-600 menu-hover-bg menu-active-bg fw-semibold fs-6" @click="scrollToContent">

                                <!-- Un groupe par thème, comme la fiche boutique ; ses pages sont les onglets au-dessus du contenu -->
                                <div v-for="group in menuGroups" :key="group.key" class="menu-item mb-1">
                                    <router-link class="menu-link px-3 py-2 rounded" :to="navigateRoute(group.items[0].route)"
                                        :class="activeGroup && activeGroup.key === group.key ? 'active bg-light-primary text-primary' : ''">
                                        <span class="store-menu-icon icon-primary"><i :class="group.icon"></i></span>
                                        <span class="menu-title d-flex align-items-center justify-content-between w-100">
                                            {{ $t(group.label) }}
                                            <span>
                                                <span v-for="item in group.items.filter(i => i.badge > 0)" :key="item.route" class="badge ms-1" :class="item.badgeClass">{{ item.badge }}</span>
                                            </span>
                                        </span>
                                    </router-link>
                                </div>

                            </div>
                        </div>
                    </div>
                </div>

                <div ref="storeContent" class="col-lg-9 col-md-9 store-content">
                    <!-- Onglets du groupe ouvert (Stock : Entrées / sorties, Entrées non validées, Inventaires) -->
                    <div v-if="activeGroup && activeGroup.items.length > 1" class="card card-flush mb-5">
                        <div class="card-body py-0 px-5">
                            <ul class="nav nav-stretch nav-line-tabs nav-line-tabs-2x border-transparent fs-6 fw-bold flex-nowrap" style="overflow-x: auto; overflow-y: hidden;">
                                <li v-for="item in activeGroup.items" :key="item.route" class="nav-item">
                                    <router-link class="nav-link text-active-primary py-4 me-6 text-nowrap" :to="navigateRoute(item.route)"
                                        :class="{ active: item.active.includes($route.name) }">
                                        {{ $t(item.label) }}
                                        <span v-if="item.badge > 0" class="badge ms-2" :class="item.badgeClass">{{ item.badge }}</span>
                                    </router-link>
                                </li>
                            </ul>
                        </div>
                    </div>
                    <router-view></router-view>
                </div>
            </div>

            <div class="modal fade" id="updateModal" data-backdrop="static" tabindex="-1" role="dialog"
                aria-labelledby="staticBackdrop" aria-hidden="true">
                <div class="modal-dialog">
                    <div class="modal-content">
                        <div class="modal-header">
                            <h5 class="modal-title">{{ $t('store.update.title') }}</h5>
                            <div class="btn btn-icon btn-sm btn-active-light-primary ms-2" data-bs-dismiss="modal"
                                aria-label="Close">
                                <span class="svg-icon svg-icon-1">
                                    <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24"
                                        fill="none">
                                        <rect opacity="0.5" x="6" y="17.3137" width="16" height="2" rx="1"
                                            transform="rotate(-45 6 17.3137)" fill="currentColor" />
                                        <rect x="7.41422" y="6" width="16" height="2" rx="1" transform="rotate(45 7.41422 6)"
                                            fill="currentColor" />
                                    </svg>
                                </span>
                            </div>
                        </div>
                        <div class="modal-body">
                            <form @submit.prevent="submitUpdate" id="kt_modal_new_target_form" class="form">

                                <div class="d-flex flex-column mb-8 fv-row">
                                    <label class="d-flex align-items-center fs-6 fw-bold mb-2">
                                        <span class="required">{{ $t('store.form.name') }}</span>
                                        <i class="fas fa-exclamation-circle ms-2 fs-7" data-bs-toggle="tooltip"
                                            title="Specify a target name for future usage and reference"></i>
                                    </label>
                                    <input type="text" class="form-control form-control-solid" v-model="formStore.name"
                                        :placeholder="$t('store.form.name')" required />
                                </div>

                                <div class="d-flex flex-column mb-8 fv-row">
                                    <label class="d-flex align-items-center fs-6 fw-bold mb-2">
                                        <span class="required">{{ $t('store.form.address') }}</span>
                                        <i class="fas fa-exclamation-circle ms-2 fs-7" data-bs-toggle="tooltip"
                                            title="Veuillez bien renseigner ce champ"></i>
                                    </label>
                                    <input type="text" class="form-control form-control-solid" v-model="formStore.address"
                                        :placeholder="$t('store.form.address')" required />
                                </div>

                              <InputTel
                                  name="editStorePhone"
                                  type="text"
                                  :label="$t('store.form.telephone')"
                                  containerClass="d-flex flex-column mb-8 fv-row"
                                  labelClass="fs-6 fw-bold mb-2"
                                  :inputClass="'form-control form-control-solid'"
                                  v-model:phone="formStore.phone"
                                  v-model:phone-code="formStore.phone_code"
                                  :placeholder="$t('enter_phone')"
                                  :isRequired="false" />


                              <div class="row">
                                <div class="col-6">
                                  <div class="d-flex flex-column mb-8 fv-row">
                                    <label class="d-flex align-items-center fs-6 fw-bold mb-2">
                                      <span>{{ $t('store.form.prefixe') }}</span>
                                    </label>
                                    <input type="text"  class="form-control form-control-solid" v-model="formStore.prefixe"
                                           :placeholder="$t('store.placeholder.prefixe')" />
                                  </div>
                                </div>
                                <div class="col-6">
                                  <div class="d-flex flex-column mb-8 fv-row">
                                    <label class="d-flex align-items-center fs-6 fw-bold mb-2">
                                      <span>{{ $t('store.form.transfer_number') }}</span>
                                    </label>
                                    <input type="text"  class="form-control form-control-solid" v-model="formStore.transfer_number"
                                           :placeholder="$t('store.placeholder.transfer_number')" />
                                  </div>
                                </div>

                              </div>
                              <div class="col-12 my-1">
                                <label class="d-flex align-items-center fs-6 fw-bold">
                                  <span class="">{{ $t('shop.status') }}</span>
                                </label>
                                <!--end::Label-->
                                <select class="form-select  kt_select2 form-control form-control-solid" name="active" v-model="formStore.etat"
                                        data-control="select2" data-dropdown-parent="#updateModal" data-search="true"
                                        data-placeholder="Select an option">
                                  <option></option>
                                  <option :value="'active'">{{ $t('shop.active') }}</option>
                                  <option :value="'inactive'">{{ $t('shop.inactive') }}</option>
                                </select>
                              </div>
                                <div class="modal-footer">
                                    <button type="button" class="btn btn-sm btn-light" data-bs-dismiss="modal">{{
                                        $t('button.close')
                                    }}</button>
                                    <button type="submit" class="btn btn-sm btn-primary" :disabled="isLoading">
                                        <span class="indicator-label" v-if="!isLoading">{{ $t('button.save') }}</span>
                                        <span class="" v-else>{{ $t('loading') }}
                                            <span class="spinner-border spinner-border-sm align-middle ms-2"></span></span>
                                    </button>
                                </div>
                            </form>
                        </div>
                    </div>
                </div>
            </div>
        </div>
        <div class="card mb-5 mb-xl-10" v-show="!isReady">
            <Loader />
        </div>
    </div>
</template>

<script>
import { mapGetters, mapActions } from 'vuex'
import { VueTelInput } from 'vue-tel-input'
import 'vue-tel-input/dist/vue-tel-input.css';
import InputTel from "@/components/InputTel.vue";
import Loader from "@/components/Loader.vue";
export default {
    name: "StoreView",
    components: {InputTel, VueTelInput, Loader },

    data() {
        return {
            title: i18n.global.t('breadcrumbs.store.path'),
            isLoading: false,
            dialCode:'',
            phoneNumber:'',
            formStore: {
                id: '',
                name: '',
                address: '',
                phone: '',
                phone_code: '',
                prefixe: '',
                etat: '',
                transfer_number: ''
            },
            etatStore: {
                id: '',
                etat: ''
            },
            store: {},
            breadcrumbs: [
                {
                    name: i18n.global.t('breadcrumbs.store.dashboard'),
                    path: '/admin/dashboard'
                },
                {
                    name: i18n.global.t('breadcrumbs.store.path'),
                    path: '/admin/stores'
                },

                {
                    name: i18n.global.t('breadcrumbs.store.pathDetails'),
                    path: '/admin/stores/' + this.$route.params.id + '/views'
                }
            ],
            isUsing: true,
            isReady: false
        }
    },

    computed: {
        ...mapGetters('store', [
            'getStore',
            'getCurrentStore'
        ]),

        counts() {
            const store = this.store || {}
            return {
                products: store.count || 0,
                moves: (store.count_entrie || 0) + (store.count_output || 0),
                unvalidated: store.count_unvalide_entry || 0,
            }
        },

        /**
         * Menu du magasin regroupé comme la fiche boutique : Articles, Stock (onglets), Utilisateurs.
         * Une page n'apparaît que si l'utilisateur a le droit ; un groupe vide disparaît.
         */
        menuGroups() {
            const can = (permission) => this.$can(permission)
            const groups = [
                { key: 'items', label: 'store.menu.product', icon: 'bi bi-box-seam', items: [
                    { route: 'store-details', label: 'store.menu.product', show: can('product_magasin_view'), active: ['store-data-path', 'store-details', 'store-details-product'], badge: this.counts.products, badgeClass: 'badge-light-primary' },
                ] },
                { key: 'stock', label: 'shop.menu_groups.stock', icon: 'bi bi-boxes', items: [
                    { route: 'store-details-entries', label: 'store.menu.enter_out', show: can('product_magasin_transfert_view'), active: ['store-details-entries', 'store-details-output', 'store-entries-create', 'store-output-create', 'store-output-content'], badge: this.counts.moves, badgeClass: 'badge-light-warning' },
                    { route: 'store-details-unvalide-entry', label: 'store.menu.unvalide_entry', show: can('no_validate_entry_view'), active: ['store-details-unvalide-entry', 'store-unvalid-entry-view'], badge: this.counts.unvalidated, badgeClass: 'badge-light-danger' },
                    { route: 'store-inventories', label: 'store.menu.inventories', show: can('inventory_magasin_view'), active: ['store-inventories', 'store-inventory-detail'] },
                ] },
                { key: 'team', label: 'store.menu.user', icon: 'bi bi-people', items: [
                    { route: 'store-details-user', label: 'store.menu.user', show: can('user_magasin_view'), active: ['store-details-user', 'store-details-update'] },
                ] },
            ]

            return groups
                .map((group) => ({ ...group, items: group.items.filter((item) => item.show) }))
                .filter((group) => group.items.length)
        },

        activeGroup() {
            return this.menuGroups.find((group) => group.items.some((item) => item.active.includes(this.$route.name))) || null
        },

        options(){
            return {
                placeholder: i18n.global.t('enter_phone'),
                showDialCode : false
            }
        },

        dropdownOptions(){
            return {
                showDialCodeInSelection: true,
                showDialCodeInList: true,
                showFlags: true
            }
        },


    },

    async mounted() {
      this.loadData()
      $("#updateModal").on("shown.bs.modal", function () {
        $('.kt_select2').select2();
      });
      $('.kt_select2').change((e) => {
        switch (e.target.name) {
          case 'active':
            this.formStore.etat = e.target.value
            break;
          default:
            break;
        }
      });
    },

    methods: {
        ...mapActions('store', ['updateStore', 'showStore', 'deleteStore', 'archiveStore', 'unarchiveStore']),

        // Sur mobile le menu est empilé au-dessus du contenu : après un clic on descend sur la page choisie
        scrollToContent(e) {
          if (!e.target.closest('.menu-link') || window.innerWidth >= 768) return
          const el = this.$refs.storeContent
          if (!el) return
          // La page choisie se charge après coup : on réserve un écran au contenu pour que
          // le navigateur ne bloque pas le scroll avant, et on scrolle sans animation
          // (en 'smooth' l'animation reste figée pendant le chargement de la page)
          el.style.minHeight = 'calc(100vh - 75px)'
          el.scrollIntoView({ behavior: 'instant', block: 'start' })
        },

        navigateRoute(link) {
          return {name: link, params: {id: this.$route.params.id}}
        },

        async loadData() {

          this.isReady = false
          let response = await this.showStore(this.$route.params.id)
          this.store = response.data.store
          this.isUsing = (this.store.count_entrie === 0) && (this.store.count_output === 0) && (this.store.count === 0);
          this.isReady = true
        },

        async submitUpdate() {
            this.isLoading = true;
            try {
                await this.updateStore({...this.formStore, phone: this.formStore.phone_code + ' ' + this.formStore.phone});
                let response = await this.showStore(this.$route.params.id)
                this.store = response.data.store
                window.toastr['success'](i18n.global.t('notification.store.updated'))
                $('#updateModal').modal('toggle')
                this.isLoading = false;
            } catch (err) {
                //window.toastr['error'](err)
                this.isLoading = false;
            }
        },

        async startModalUpdate() {
            const phoneCode = this.store?.phone?.split(" ")[0] || ''

            this.formStore.id = this.store.id;
            this.formStore.name = this.store.name;
            this.formStore.address = this.store.address;
            this.formStore.phone_code = phoneCode
            this.formStore.phone = this.store?.phone?.replace(phoneCode, '') || ''
            this.formStore.prefixe = this.store.prefixe;
            this.formStore.etat = this.store.etat;
            this.formStore.transfer_number = this.store.transfer_number;
            $('#updateModal').modal('toggle')
        },
        async active() {
            this.etatStore.id = this.$route.params.id
            this.etatStore.etat = 'active'
            await this.unarchiveStore(this.etatStore)
            window.toastr['success']("Magasin désarchivé avec succès")

            this.$router.push({ name: 'store-details-product', params: { id: this.$route.params.id } })
            this.loadData()
        },

        async remove() {
            Swal.fire({
                title: i18n.global.t('store.swal_title'),
                text: i18n.global.t('store.swal_text'),
                icon: "warning",
                showCancelButton: true,
                confirmButtonText: i18n.global.t('store.output.yes'),
                cancelButtonText: i18n.global.t('store.output.no'),
            }).then(async (result) => {
                if (result.value) {
                    await this.deleteStore(this.$route.params.id)
                    this.$router.push({ name: 'settings-stores' })
                    window.toastr['success'](i18n.global.t('store.remove_success'))
                }
            });
        },

        async archive() {
            Swal.fire({
                title: i18n.global.t('store.swal_title_archive'),
                text: i18n.global.t('store.swal_text_archive'),
                icon: "warning",
                showCancelButton: true,
                confirmButtonText: i18n.global.t('store.output.yes'),
                cancelButtonText: i18n.global.t('store.output.no'),
            }).then(async (result) => {
                if (result.value) {
                    this.etatStore.id = this.$route.params.id
                    this.etatStore.etat = 'archive'
                    await this.archiveStore(this.etatStore)
                    this.$router.push({ name: 'settings-stores' })
                    window.toastr['success'](i18n.global.t('store.archive_success'))
                }
            });
        },


        thirstInputCountryChanged(country) {
                this.dialCode = '+' + country.dialCode
        },

        thirstInputChanged(number, phoneObject) {
            let errorDiv = document.querySelector('[data-field="store_phone"][data-field="store_phone"][data-validator="callback"]')
            if (errorDiv) {
                errorDiv.remove()
            }

            if (phoneObject) {
                //this.formStore.phone = phoneObject.nationalNumber
                //phoneObject.countryCallingCode=this.store.phone.split(" ")[0]
            }
        }
    }
}
</script>

<style scoped>
/* laisse la place de l'en-tête fixe quand on défile vers le contenu (mobile) */
.store-content {
    scroll-margin-top: 75px;
}

/* Icônes du menu gauche : mêmes pastilles que la fiche boutique */
.store-menu-icon {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 28px;
    height: 28px;
    border-radius: 6px;
    margin-right: 10px;
    font-size: 0.95rem;
    flex-shrink: 0;
    transition: all 0.2s ease;
}
/* Icônes : une seule couleur (celle de la boutique), comme le menu principal ;
   les classes icon-* restent sur les entrées mais ne changent plus la couleur */
.store-menu-icon.icon-primary,
.store-menu-icon.icon-success,
.store-menu-icon.icon-warning,
.store-menu-icon.icon-danger,
.store-menu-icon.icon-info,
.store-menu-icon.icon-dark {
  background: rgba(var(--kt-primary-rgb), 0.12);
  color: var(--kt-primary);
}

/* le thème grise les <i> du menu : l'icône prend la couleur de son cadre */
.store-menu-icon i {
  color: inherit !important;
}
</style>
